/**
 * Paylaşım linkleri yönetimi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LİNK BİR KEZ GÖSTERİLİR — ve bu bir eksiklik değil, kararın kendisi.
 *
 * Veritabanında token'ın yalnız sha256'sı duruyor [S14]; "linki bir daha
 * göster" özelliği eklemek, ham token'ı saklamayı gerektirir ve hash'li
 * saklamanın TÜM faydasını siler. Depo sızarsa bugün hiçbir link
 * kullanılamaz — bu, kaybedilen linkin yeniden üretilmesi zahmetine değer.
 *
 * Ekran bu yüzden linki oluşturur oluşturmaz gösteriyor, kopyalamayı
 * kolaylaştırıyor ve kapatınca bir daha göstermeyeceğini SÖYLÜYOR.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, istek } from '../lib/api.ts';
import { Badge } from '../design/Status.tsx';
import { Button } from '../design/Button.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { Field, Input, Select } from '../design/Field.tsx';
import { KapsamNotu, YapilandirmaSekmeleri } from '../field/parts.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { Yetki } from '../auth/Yetki.tsx';
import { gecenSure } from '../field/okuma.ts';
import { useSahalar } from '../field/api.ts';

interface Link {
  readonly id: number;
  readonly label: string | null;
  readonly site_ids: readonly number[];
  readonly is_active: boolean;
  readonly expires_at: string | null;
  readonly last_viewed_at: string | null;
  readonly view_count: number;
  readonly created_at: string;
  readonly revoked_at: string | null;
  readonly created_by_name: string;
}

export function PaylasimLinkleri(): React.JSX.Element {
  const qc = useQueryClient();
  const [yeniToken, setYeniToken] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [etiket, setEtiket] = useState('');
  const [sahaId, setSahaId] = useState('');
  const [gun, setGun] = useState('');

  const sorgu = useQuery({
    queryKey: ['paylasim-linkleri'],
    queryFn: async () => (await istek<{ links: Link[] }>('/share-links')).links,
  });
  const sahalar = useSahalar();

  const olustur = useMutation({
    mutationFn: async () => {
      const govde: Record<string, unknown> = {};
      if (etiket.trim() !== '') govde['label'] = etiket.trim();
      if (sahaId !== '') govde['siteIds'] = [Number(sahaId)];
      if (gun !== '') {
        govde['expiresAt'] = new Date(Date.now() + Number(gun) * 86_400_000).toISOString();
      }
      return await istek<{ id: number; token: string }>('/share-links', {
        method: 'POST',
        body: govde,
      });
    },
    onSuccess: (sonuc) => {
      setYeniToken(sonuc.token);
      setHata(null);
      setEtiket('');
      void qc.invalidateQueries({ queryKey: ['paylasim-linkleri'] });
    },
    onError: (e: unknown) => {
      setHata(e instanceof ApiError ? e.message : 'Link oluşturulamadı.');
    },
  });

  const iptal = useMutation({
    mutationFn: async (id: number) =>
      await istek(`/share-links/${String(id)}/iptal`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['paylasim-linkleri'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Paylaşım linkleri"
        description="Oturum gerektirmeyen izleme bağlantıları"
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />

      <PageBody>
        <div className="flex flex-col gap-4">
          {/* ── Yeni link ──────────────────────────────────────────────── */}
          <Yetki gerekli="sharelink_manage">
            <Card>
              <CardBody>
                <h2 className="text-sm font-semibold text-ink">Yeni link</h2>
                <div className="mt-3 flex flex-col gap-3">
                  <Field label="Etiket" hint="Kime verildiğini hatırlamak için">
                    {({ id }) => (
                      <Input
                        id={id}
                        value={etiket}
                        onChange={(e) => setEtiket(e.target.value)}
                        placeholder="Örn. Limak — Tatvan görünümü"
                      />
                    )}
                  </Field>

                  <Field label="Saha" hint="Boş bırakılırsa firmanın TÜM sahaları görünür">
                    {({ id }) => (
                      <Select id={id} value={sahaId} onChange={(e) => setSahaId(e.target.value)}>
                        <option value="">Tüm sahalar</option>
                        {(sahalar.data ?? []).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>

                  <Field label="Geçerlilik" hint="Süresiz link, unutulduğunda açık kalır">
                    {({ id }) => (
                      <Select id={id} value={gun} onChange={(e) => setGun(e.target.value)}>
                        <option value="">Süresiz</option>
                        <option value="7">7 gün</option>
                        <option value="30">30 gün</option>
                        <option value="90">90 gün</option>
                      </Select>
                    )}
                  </Field>

                  {hata !== null && (
                    <p role="alert" className="text-sm text-danger">
                      {hata}
                    </p>
                  )}

                  <div>
                    <Button loading={olustur.isPending} onClick={() => olustur.mutate()}>
                      Link oluştur
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Yetki>

          {/* ── Yeni üretilen token — BİR KEZ ──────────────────────────── */}
          {yeniToken !== null && (
            <div
              role="status"
              className="rounded-[var(--radius-card)] border border-brand-200 bg-brand-50 p-3"
            >
              <p className="text-sm font-medium text-brand-700">
                Link hazır — bu bağlantı bir daha gösterilmeyecek.
              </p>
              <p className="mt-1 break-all rounded bg-surface p-2 font-mono text-xs text-ink">
                {`${window.location.origin}/izleme/${yeniToken}`}
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard?.writeText(
                      `${window.location.origin}/izleme/${yeniToken}`,
                    );
                  }}
                >
                  Kopyala
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setYeniToken(null)}>
                  Kapat
                </Button>
              </div>
            </div>
          )}

          {/* ── Mevcut linkler ─────────────────────────────────────────── */}
          {sorgu.isPending && <SkeletonList rows={2} />}

          {sorgu.isError && (
            <EmptyState
              title="Linkler yüklenemedi"
              description={sorgu.error instanceof Error ? sorgu.error.message : 'Bilinmeyen hata.'}
              action={<Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>}
            />
          )}

          {sorgu.data !== undefined &&
            (sorgu.data.length === 0 ? (
              <EmptyState
                title="Henüz paylaşım linki yok"
                description="Müşterinize oturum açmadan izleyebileceği bir bağlantı verebilirsiniz."
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {sorgu.data.map((link) => (
                  <li key={link.id}>
                    <LinkKarti link={link} iptalEt={() => iptal.mutate(link.id)} />
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </PageBody>
    </>
  );
}

function LinkKarti({
  link,
  iptalEt,
}: {
  readonly link: Link;
  readonly iptalEt: () => void;
}): React.JSX.Element {
  const suresiGecti = link.expires_at !== null && new Date(link.expires_at) < new Date();
  const gecerli = link.is_active && link.revoked_at === null && !suresiGecti;

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-ink">{link.label ?? 'Etiketsiz link'}</p>
            <p className="mt-0.5 text-sm text-ink-muted">
              {link.site_ids.length === 0 ? 'Tüm sahalar' : `${String(link.site_ids.length)} saha`}
              {' · '}
              {link.created_by_name}
            </p>
          </div>
          {/*
           * Durum METİNLE: "pasif" bir linkin neden çalışmadığını sahibi
           * bilmeli — süresi mi doldu, iptal mi edildi.
           */}
          <Badge tone={gecerli ? 'ok' : 'neutral'}>
            {link.revoked_at !== null ? 'İptal edildi' : suresiGecti ? 'Süresi doldu' : 'Aktif'}
          </Badge>
        </div>

        <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
          <div className="flex gap-1">
            <dt>Görüntülenme:</dt>
            <dd className="font-medium text-ink">{link.view_count}</dd>
          </div>
          {link.last_viewed_at !== null && (
            <div className="flex gap-1">
              <dt>Son:</dt>
              <dd className="font-medium text-ink">
                {gecenSure(link.last_viewed_at, new Date().toISOString()) ?? '—'}
              </dd>
            </div>
          )}
          {link.expires_at !== null && (
            <div className="flex gap-1">
              <dt>Bitiş:</dt>
              <dd className="font-medium text-ink">
                {new Date(link.expires_at).toLocaleDateString('tr-TR')}
              </dd>
            </div>
          )}
        </dl>

        {gecerli && (
          <Yetki gerekli="sharelink_manage">
            <div className="mt-3">
              <Button size="sm" variant="danger" onClick={iptalEt}>
                İptal et
              </Button>
            </div>
          </Yetki>
        )}
      </CardBody>
    </Card>
  );
}
